import React from 'react';
import { throttle } from 'underscore';
import useFontLoadedListener from 'hooks/useFontLoadedListener';

import { TextOverlayResizeObserver } from '../../hooks/useTextOverlayResizeDelta';
import { EditorDataV2, Size } from '../../types';
import {
  AdjustedText,
  BoxSizeFitChecker,
  BoxSizeFitCheckerConfig,
} from './types';
import {
  getBoxMaxLines,
  shouldDisableSubmitButton,
  shouldShowRechunkAlert,
} from './utils';

interface UseBoxSizeFitCheckerConfig {
  baseTextValue: string;
  canvasSize: Size;
  draftEditorData: EditorDataV2;
  extensionConfig: BoxSizeFitCheckerConfig;
  initialEditorData: EditorDataV2;
}

type UseBoxSizeFitChecker = BoxSizeFitChecker;

const BASE_RESIZE_DELTA: Size = { height: 0, width: 0 };
const RESIZE_THROTLE_MS = 2.5;

const useBoxSizeFitChecker = (
  config: UseBoxSizeFitCheckerConfig,
): UseBoxSizeFitChecker => {
  const {
    baseTextValue,
    draftEditorData,
    extensionConfig = {},
    initialEditorData,
  } = config;

  const {
    enabled = false,
    onTextRangeChange,
    onToggleRechunkRequired,
    rechunkAlert,
    submitDisabledOnNoFit,
  } = extensionConfig;

  const [adjustedText, setAdjustedText] = React.useState<AdjustedText>({
    value: baseTextValue,
    isOverflowing: false,
  });

  const getAdjustedText = React.useCallback(
    (updatedDelta: Size, allowSingleWordOverflow: boolean = true): string => {
      const [textLineStartPost, textLineEndPost] = getBoxMaxLines(
        baseTextValue,
        draftEditorData,
        updatedDelta,
        allowSingleWordOverflow,
      );

      return baseTextValue.substring(textLineStartPost, textLineEndPost).trim();
    },
    [baseTextValue, draftEditorData],
  );

  const handleAdjustTextValue: TextOverlayResizeObserver = React.useMemo(
    () =>
      throttle((updatedDelta = BASE_RESIZE_DELTA, opts): void => {
        if (!enabled || opts?.type === 'stop') {
          return;
        }

        // We call `getAdjustedText` twice for different purposes:
        // - `nextAdjustedText` is used for the preview. In this case, we do want to allow overflow,
        //   so the user can visually see that the text is too large or doesn't fit.
        const nextAdjustedText = getAdjustedText(updatedDelta);
        // - `nextAdjustedTextWithoutOverflow` is used for validation logic — specifically to determine
        //   if the submit button should be disabled. It checks if the current text (even if it's just
        //   a single word) is overflowing, and updates `setIsCurrentTextOverflowing` accordingly.
        const nextAdjustedTextWithoutOverflow = getAdjustedText(
          updatedDelta,
          false,
        );

        setAdjustedText({
          value: nextAdjustedText,
          isOverflowing: !nextAdjustedTextWithoutOverflow,
        });
      }, RESIZE_THROTLE_MS),
    [enabled, getAdjustedText],
  );

  React.useEffect(() => {
    requestAnimationFrame(() => {
      handleAdjustTextValue();
    });
  }, [handleAdjustTextValue, draftEditorData]);

  React.useEffect(() => {
    if (!enabled) {
      setAdjustedText({
        value: baseTextValue,
        isOverflowing: false,
      });
    }
  }, [baseTextValue, enabled]);

  const { submitDisabled, submitDisabledMessage } = React.useMemo(() => {
    const disabled = shouldDisableSubmitButton(
      adjustedText.value,
      adjustedText.isOverflowing,
      submitDisabledOnNoFit?.enabled,
    );
    const message = disabled ? submitDisabledOnNoFit?.message : undefined;

    return {
      submitDisabled: disabled,
      submitDisabledMessage: message,
    };
  }, [
    adjustedText.isOverflowing,
    adjustedText.value,
    submitDisabledOnNoFit?.enabled,
    submitDisabledOnNoFit?.message,
  ]);

  const { showRechunkAlert, rechunkAlertMessage } = React.useMemo(() => {
    const show = shouldShowRechunkAlert(
      initialEditorData,
      draftEditorData,
      rechunkAlert?.enabled,
    );
    const message = show ? rechunkAlert?.message : undefined;

    return {
      showRechunkAlert: show,
      rechunkAlertMessage: message,
    };
  }, [draftEditorData, initialEditorData, rechunkAlert]);

  React.useEffect(() => {
    onTextRangeChange?.([0, adjustedText.value.length]);
  }, [adjustedText.value.length, onTextRangeChange]);

  React.useEffect(() => {
    onToggleRechunkRequired?.(showRechunkAlert);
  }, [onToggleRechunkRequired, showRechunkAlert]);

  // This effect is necessary because fonts might present a delay for being loaded.
  // In screen this can be seen as a small period of time with the font set to a serif
  // font and then for it to be switched to the real font face. For that reason when
  // the loadingdone event is fired, an extra redraw is done.
  useFontLoadedListener({
    onFontLoadingDone: React.useCallback(() => handleAdjustTextValue(), [
      handleAdjustTextValue,
    ]),
  });

  return {
    resizeDeltaObserver: handleAdjustTextValue,
    rechunkAlertMessage,
    showRechunkAlert,
    submitDisabled,
    submitDisabledMessage,
    textValue: adjustedText.value,
  };
};

export default useBoxSizeFitChecker;
